import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import TopHeader from "../../Components/TopHeader";
import SearchBar from "../../Components/common/SearchBar";
import OrganisationLayout from "../layout/OrganisationLayout";
import {
  organisationGroupsListAction,
  organisationStaffListAction,
  organisationAssignRoleAction,
  organisationUpdateRoleAction,
  organisationMemberDetailsAction,
} from "../../Redux/Action/organisationAction";
import { getActiveGroup, getOrganisationRoleAccess } from "../utils/roleAccess";
import { OrganisationMemberDetailsModal } from "./Members";
import ConfirmModal from "../../modals/ConfirmModal";
const getStaffRows = (payload) => {
  const data = payload?.data || payload || {};
  const staff = data?.staff || data?.rows || data?.docs || [];
  return Array.isArray(staff) ? staff : [];
};

const getStaffMeta = (payload, fallbackPage = 0, fallbackLimit = 10) => {
  const data = payload?.data || payload || {};
  const total = Number(data?.total || 0);
  const limit = Number(data?.limit || fallbackLimit || 10);

  return {
    total,
    page: Number.isFinite(Number(data?.page))
      ? Number(data?.page)
      : fallbackPage,
    limit,
    totalPages:
      Number(data?.totalPages) || Math.ceil(total / (limit || 10)) || 0,
  };
};

const getFullName = (staff) =>
  [staff?.title, staff?.firstName, staff?.surName].filter(Boolean).join(" ") ||
  "-";

const getSchoolName = (staff) =>
  staff?.school?.schoolName || staff?.schoolName || "-";

const formatDisplayDateTime = (value) => {
  if (!value) return "Never";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",


  });
};

export default function OrganisationGroupsStaff() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const access = getOrganisationRoleAccess(userData);
  const canAssignRoles = access.isAdmin || access.isSuperAdmin;
  const navigate = useNavigate();
  const organisationSchoolId =
    getActiveGroup(userData)?._id || userData?.schoolId || "";

  const [searchValue, setSearchValue] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState("");
  const [selectedGroupRole, setSelectedGroupRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [tableMeta, setTableMeta] = useState({
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  });
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [assigningId, setAssigningId] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRoleMember, setSelectedRoleMember] = useState(null);
  const [selectedRoleToAssign, setSelectedRoleToAssign] = useState("");

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const [organisationOptions, setOrganisationOptions] = useState([]);

  useEffect(() => {
    if (!organisationSchoolId) return;
    dispatch(
      organisationGroupsListAction({
        groupId: organisationSchoolId,
        page: 0,
        limit: 100,
        search: "",
      }),
    ).then((res) => {
      const payload = res?.payload?.data;
      const orgs =
        payload?.organisations ||
        payload?.organisationsList ||
        payload?.rows ||
        payload?.data ||
        [];
      setOrganisationOptions(Array.isArray(orgs) ? orgs : []);
    });
  }, [dispatch, organisationSchoolId]);

  const filteredStaff = useMemo(() => {
    return tableData.filter(
      (item) =>
        (!selectedOrganisation ||
          (item?.school?._id || item?.schoolId?._id || item?.schoolId) ===
          selectedOrganisation) &&
        (!selectedAccountType || item?.accountType === selectedAccountType) &&
        (!selectedGroupRole || item?.groupRole === selectedGroupRole) &&
        (!selectedStatus || item?.status === selectedStatus),
    );
  }, [selectedOrganisation, selectedAccountType, selectedGroupRole, selectedStatus, tableData]);

  const loadMemberDetails = async (member) => {
    const memberId = member?._id || member?.memberId || member?.id || member?.userId || "";
    if (!memberId) return member;

    const response = await dispatch(organisationMemberDetailsAction(memberId));
    if (response?.error) {
      toast.error(response?.payload?.message || "Unable to load member details right now.");
      return member;
    }

    const payload = response?.payload?.data || response?.payload || {};
    const memberDetails = payload?.member || payload?.memberDetails || payload?.details || payload?.data || payload || {};

    return { ...member, ...memberDetails };
  };

  const handleDetailClick = async (data) => {
    if (data?.accountType === "Organisation Group Member") {
      setShowDetailsModal(true);
      setIsDetailLoading(true);
      setDetailMember(data);
      const detailedMember = await loadMemberDetails(data);
      setDetailMember(detailedMember);
      setIsDetailLoading(false);
    } else {
      navigate(`/organisations/staff-detail/${data?._id}/${data?.accountType}`);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailMember(null);
  };

  const handleOrganisationSelect = (e) => {
    setSelectedOrganisation(e.target.value);
  };

  const handleAccountTypeSelect = (e) => {
    setSelectedAccountType(e.target.value);
  };

  const handleGroupRoleSelect = (e) => {
    setSelectedGroupRole(e.target.value);
  };

  const handleStatusSelect = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortBy(field);
    setSortOrder(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const selectedOrganisationSchoolId = selectedOrganisation || "";

  const confirmRoleAssignment = (row, role) => {
    setSelectedRoleMember(row);
    setSelectedRoleToAssign(role);
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setSelectedRoleMember(null);
    setSelectedRoleToAssign("");
  };

  const handleSaveRole = async () => {
    if (!selectedRoleMember || !selectedRoleToAssign) {
      toast.error("Please select a role to assign");
      return;
    }

    setAssigningId(selectedRoleMember?._id);
    const payload = {
      staffId: selectedRoleMember?._id,
      role: selectedRoleToAssign,
      memberType: (selectedRoleMember?.accountType || "").replace(/\s+/g, ""),
    };

    let response;
    const isEdit = selectedRoleMember?.groupRole && selectedRoleMember?.groupRole !== "-" && selectedRoleMember?.groupRole !== "group_no_permission";

    if (isEdit) {
      response = await dispatch(organisationUpdateRoleAction(payload));
    } else {
      response = await dispatch(organisationAssignRoleAction(payload));
    }

    setAssigningId("");

    if (!response?.error) {
      toast.success(response?.payload?.message || `Role ${isEdit ? 'updated' : 'assigned'} successfully`);
      handleCloseRoleModal();
      setRefreshFlag((prev) => prev + 1);
    } else {
      toast.error(response?.payload?.message || `Failed to ${isEdit ? 'update' : 'assign'} role`);
    }
  };

  useEffect(() => {
    const fetchStaff = async () => {
      setIsLoading(true);
      setLoadError(null);

      const response = await dispatch(
        organisationStaffListAction({
          page,
          limit: tableMeta.limit || 10,
          search: searchValue.trim(),
          schoolId: selectedOrganisationSchoolId,
          accountType: selectedAccountType,
          ogMemberRole: selectedGroupRole,
          status: selectedStatus,
          sortBy,
          sortOrder,
        }),
      );

      console.log("organisationStaffListAction response", response?.payload);

      if (response?.error) {
        setLoadError(response?.payload || response?.error || null);
        toast.error(
          response?.payload?.message || "Unable to load organisation staff.",
        );
        setTableData([]);
        setTableMeta({
          total: 0,
          page,
          limit: tableMeta.limit || 10,
          totalPages: 0,
        });
        setIsLoading(false);
        return;
      }

      setTableData(getStaffRows(response?.payload));
      setTableMeta(
        getStaffMeta(response?.payload, page, tableMeta.limit || 10),
      );
      setIsLoading(false);
    };

    fetchStaff();
  }, [
    dispatch,
    organisationSchoolId,
    page,
    searchValue,
    tableMeta.limit,
    selectedOrganisationSchoolId,
    selectedAccountType,
    selectedGroupRole,
    selectedStatus,
    sortBy,
    sortOrder,
    refreshFlag,
  ]);

  const totalPages = tableMeta.totalPages || 0;
  const pageStart = tableMeta.total === 0 ? 0 : page * tableMeta.limit + 1;
  const pageEnd = Math.min((page + 1) * tableMeta.limit, tableMeta.total);

  return (
    <OrganisationLayout>
      <TopHeader setSearchValue={setSearchValue} groupData={[]} type="Class" />

      <div className="heading-top mt-5 mb-2">
        <h2>All Staff</h2>
      </div>
      <Container fluid>
        <Row>
          <Col lg={3}>
            <SearchBar setSearchValue={setSearchValue} />
          </Col>
          <Col lg={3}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Default select example"
                className="cmn-select"
                onChange={handleOrganisationSelect}
                value={selectedOrganisation}
              >
                <option value="">All Organisations</option>
                {organisationOptions?.map((item) => (
                  <option
                    value={item?.organisationId}
                    key={item?.organisationId}
                  >
                    {item?.organisationName ||
                      item?.schoolId?.schoolName ||
                      item?.schoolName ||
                      "-"}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
          <Col lg={2}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Account Type"
                className="cmn-select"
                onChange={handleAccountTypeSelect}
                value={selectedAccountType}
              >
                <option value="">All Account Types</option>
                <option value="Teacher">Teacher</option>
                <option value="School Admin">School Admin</option>

              </Form.Select>
            </div>
          </Col>
          <Col lg={2}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Group Role"
                className="cmn-select"
                onChange={handleGroupRoleSelect}
                value={selectedGroupRole}
              >
                <option value="">All Group Roles</option>
                <option value="group_admin">Group Admin</option>
                <option value="group_collaborator">Group Collaborator</option>
              </Form.Select>
            </div>
          </Col>
          <Col lg={2}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Status"
                className="cmn-select"
                onChange={handleStatusSelect}
                value={selectedStatus}
              >
                <option value="">Filter by Status</option>
                <option value="Inactive">Inactive</option>
                <option value="Created">Created</option>
                <option value="Active">Active</option>
                <option value="Blocked">Blocked</option>
              </Form.Select>
            </div>
          </Col>
        </Row>
      </Container>
      <Container fluid>
        <section className="organisation-groups-table-shell organisation-groups-table-shell-space">
          <div className="organisation-panel-heading">
            <div>
              <h3>Member Org Staff</h3>
            </div>
          </div>
          <div className="organisation-table-responsive">
            <table>
              <thead>
                <tr>
                  <th className="pb-5 wrap-header min-w-150">
                    <span
                      className={`cursor-pointer ${sortBy === "firstName" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("firstName")}
                    >
                      Staff Member
                    </span>
                  </th>
                  <th className="pb-5 wrap-header min-w-200">Member Organisation</th>
                  <th className="pb-5 wrap-header min-w-200">Role in Member Organisation</th>
                  <th className="pb-5 wrap-header min-w-150">Role in Organisation <br /> Group</th>
                  <th className="pb-5 wrap-header min-w-100">Status</th>
                  <th className="pb-5 wrap-header min-w-140">
                    <span
                      className={`cursor-pointer ${sortBy === "createdAt" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("createdAt")}
                    >
                      Joined Peerscroller (Date)
                    </span>
                  </th>
                  <th className="pb-5 wrap-header min-w-160">
                    <span

                      className={`cursor-pointer ${sortBy === "joiningDate" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("joiningDate")}
                    >
                      Joined Organisation Group (Date)
                    </span>
                  </th>
                  <th className="pb-5 wrap-header min-w-120">
                    <span
                      className={`cursor-pointer ${sortBy === "platformVisits" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("platformVisits")}
                    >
                      Platform visits
                    </span>
                  </th>
                  <th className="pb-5 wrap-header min-w-120">
                    <span
                      className={`cursor-pointer ${sortBy === "collectionShareCount" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("collectionShareCount")}
                    >
                      Collections shared
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      Loading staff...
                    </td>
                  </tr>
                )}
                {!isLoading && loadError && (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      Unable to load organisation staff right now.
                    </td>
                  </tr>
                )}
                {!isLoading && !loadError && filteredStaff.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      No staff found.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  !loadError &&
                  filteredStaff.map((row) => (
                    <tr key={row?._id || row?.email}>
                      <td>
                        <div
                          className="organisation-person-cell cursor-pointer"
                          onClick={() => handleDetailClick(row)}
                        >
                          <img
                            className="organisation-avatar-sm"
                            src={
                              row?.image ||
                              require("../../Assets/Images/girl.png")
                            }
                            alt={getFullName(row)}
                            height="42"
                            width="42"
                          />
                          <div className="organisation-name-cell">
                            <strong>{getFullName(row)}</strong>
                            <p>{getSchoolName(row)}</p>
                          </div>
                        </div>
                      </td>
                      <td>{[row?.firstName, row?.surName].filter(Boolean).join(" ") || "-"}</td>
                      <td>{row?.accountType === "School" || row?.accountType?.toLowerCase() === "school" ? "Super Admin" : (row?.accountType || "-")}</td>
                      <td>
                        {canAssignRoles && row?.accountType !== "Organisation Group Member" && !["group_super_admin", "group super admin", "super admin", "super_admin"].includes((row?.groupRole || "").toLowerCase()) ? (
                          <Form.Select
                            className="cmn-pink cursor-pointer"
                            value={row?.groupRole && row?.groupRole !== "-" ? row?.groupRole : ""}
                            onChange={(e) => {
                              if (e.target.value) {
                                confirmRoleAssignment(row, e.target.value);
                              }
                            }}
                          >
                            <option value="" disabled style={{ backgroundColor: "#fff", color: "#333" }}>Select Role</option>
                            <option value="group_admin" style={{ backgroundColor: "#fff", color: "#333" }}>Group Admin</option>
                            <option value="group_collaborator" style={{ backgroundColor: "#fff", color: "#333" }}>Group Collaborator</option>
                            <option value="group_no_permission" style={{ backgroundColor: "#fff", color: "#333" }}>No Permission</option>
                          </Form.Select>
                        ) : (
                          <span className="text-nowrap">
                            {row?.groupRole && row?.groupRole !== "-" && row?.groupRole !== "group_no_permission" ? row?.groupRole.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "-"}
                          </span>
                        )}
                      </td>
                      <td>{row?.status || "-"}</td>
                      <td>{formatDisplayDateTime(row?.createdAt)}</td>
                      <td>{(row?.joiningDate || row?.ogJoiningDate) ? formatDisplayDateTime(row?.joiningDate || row?.ogJoiningDate) : "-"}</td>
                      <td>{row?.platformVisits ?? "-"}</td>
                      <td>{row?.collectionShareCount ?? "-"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="organisation-pagination">
            <div className="organisation-pagination-meta">
              Showing {pageStart}-{pageEnd} of {tableMeta.total || 0} staff
            </div>
            <div className="organisation-pagination-actions">
              <button
                className="organisation-pagination-btn"
                onClick={() =>
                  setPage((prevState) => Math.max(prevState - 1, 0))
                }
                disabled={page === 0 || isLoading}
              >
                Previous
              </button>
              <span className="organisation-pagination-page">
                Page {page + 1}
                {totalPages > 0 ? ` of ${totalPages}` : ""}
              </span>
              <button
                className="organisation-pagination-btn"
                onClick={() => setPage((prevState) => prevState + 1)}
                disabled={
                  isLoading ||
                  filteredStaff.length === 0 ||
                  (totalPages > 0 && page + 1 >= totalPages)
                }
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </Container>

      <OrganisationMemberDetailsModal
        show={showDetailsModal}
        member={detailMember}
        isLoading={isDetailLoading}
        onHide={handleCloseDetailsModal}
      />

      <ConfirmModal
        isModalOpen={showRoleModal}
        handleConfirmClose={handleCloseRoleModal}
        selectedType="assignOrganisationRole"
        message={
          <span>
            Are you sure you want to {selectedRoleMember?.groupRole && selectedRoleMember?.groupRole !== "-" && selectedRoleMember?.groupRole !== "group_no_permission" ? 'change the role to' : 'assign'} <strong>{selectedRoleToAssign === 'group_no_permission' ? 'No Org Group Role' : (selectedRoleToAssign ? selectedRoleToAssign.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "")}</strong> for <strong>{selectedRoleMember ? getFullName(selectedRoleMember) : ""}</strong>?
          </span>
        }
        extraFunction={handleSaveRole}
      />
    </OrganisationLayout>
  );
}
